// src/pages/CompressorPartTracker.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ResizableBox from '../components/ResizableBox';
import toast from 'react-hot-toast';
import { RefreshCw, FileText, Save, Trash2, Search, Filter } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import './CompressorPartTracker.css';

// ✅ Parça kategorileri
const PARCA_KATEGORILERI = [
  { value: 'all', label: '📋 Tüm Kategoriler' },
  { value: 'Rulman', label: '🔩 Rulmanlar' },
  { value: 'Spacer', label: '🔧 Spacerler' },
  { value: 'Dişli', label: '⚙️ Dişliler' },
  { value: 'Kama', label: '🔑 Kamalar' },
  { value: 'Conta', label: '📦 Contalar' },
  { value: 'Valf', label: '🚰 Valfler' },
  { value: 'Filtre', label: '🌀 Filtreler' },
  { value: 'Diğer', label: '📦 Diğer' }
];

// ✅ Pozisyon seçenekleri
const POZISYONLAR = [
  'Motor',
  'Ana Mil',
  'Redüktör',
  'Separatör',
  'Yağ Soğutucu',
  'Hava Soğutucu',
  'Element',
  'Kapak',
  'Gövde',
  'Diğer'
];

// ✅ Durum seçenekleri
const DURUMLAR = [
  { value: 'İyi', label: '✅ İyi', color: '#10b981' },
  { value: 'Orta', label: '⚠️ Orta', color: '#f59e0b' },
  { value: 'Kötü', label: '❌ Kötü', color: '#ef4444' },
  { value: 'Değişti', label: '🔄 Değişti', color: '#3b82f6' }
];

const CompressorPartTracker = () => {
  const [points, setPoints] = useState([]);
  const [lines, setLines] = useState([]);
  const [boxes, setBoxes] = useState([]);
  const [formData, setFormData] = useState({
    firma: '',
    model: '',
    imalYili: '',
    detayliAciklama: '',
    seriNumarasi: ''
  });
  const [showRecordsPanel, setShowRecordsPanel] = useState(false);
  const [savedRecords, setSavedRecords] = useState([]);
  const [showDetails, setShowDetails] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // ✅ YENİ: Kompresör listesi
  const [kompresorler, setKompresorler] = useState([]);
  const [showKompresorSecim, setShowKompresorSecim] = useState(false);
  const [kompresorArama, setKompresorArama] = useState('');

  // ✅ YENİ: Parça kütüphanesi
  const [parcaKutuphanesi, setParcaKutuphanesi] = useState({});
  const [showParcaKutuphanesi, setShowParcaKutuphanesi] = useState(false);
  const [parcaKategori, setParcaKategori] = useState('all');
  const [parcaArama, setParcaArama] = useState('');

  // ✅ YENİ: Firma önerileri
  const [firmalar, setFirmalar] = useState([]);
  const [showFirmaSuggestions, setShowFirmaSuggestions] = useState(false);

  // ✅ YENİ: Parça detay düzenleme
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedBoxIndex, setSelectedBoxIndex] = useState(null);

  // ✅ YENİ: Part list görünümü
  const [showPartList, setShowPartList] = useState(false);

  // ✅ YENİ: Arama ve filtreleme
  const [searchTerm, setSearchTerm] = useState('');
  const [filterKategori, setFilterKategori] = useState('all');
  const [filterDurum, setFilterDurum] = useState('all');

  const imageContainerRef = useRef(null);

  // 📥 Verileri yükle
  useEffect(() => {
    fetchRecords();
    fetchFirmalar();
    fetchKompresorler();
    fetchParcaKutuphanesi();
  }, []);

  const fetchRecords = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/records');
      if (!res.ok) throw new Error(`API error: ${res.status}`);
      const data = await res.json();
      setSavedRecords(data);
    } catch (err) {
      console.error('Error fetching records:', err);
      toast.error('❌ Kayıtlar yüklenemedi');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchFirmalar = useCallback(async () => {
    try {
      const res = await fetch('/api/kayitlar/firmalar');
      if (!res.ok) throw new Error(`API error: ${res.status}`);
      const data = await res.json();
      setFirmalar(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Firma listesi yüklenemedi:', err);
      setFirmalar([]);
    }
  }, []);

  // ✅ YENİ: Kompresör listesini çek
  const fetchKompresorler = useCallback(async () => {
    try {
      const res = await fetch('/api/kayitlar');
      if (!res.ok) throw new Error(`API error: ${res.status}`);
      const data = await res.json();
      setKompresorler(data);
    } catch (err) {
      console.error('Kompresör listesi yüklenemedi:', err);
      toast.error('❌ Kompresör listesi yüklenemedi');
    }
  }, []);

  // ✅ YENİ: Parça kütüphanesini çek
  const fetchParcaKutuphanesi = useCallback(async () => {
    try {
      const res = await fetch('/api/parca-kutuphanesi');
      if (!res.ok) throw new Error(`API error: ${res.status}`);
      const data = await res.json();
      setParcaKutuphanesi(data);
    } catch (err) {
      console.error('Parça kütüphanesi yüklenemedi:', err);
      // Varsayılan boş kütüphane
      setParcaKutuphanesi({
        rulmanlar: [],
        spacerler: [],
        disliler: [],
        kamalar: [],
        contalar: [],
        valfler: [],
        filtreler: [],
        diger: []
      });
    }
  }, []);

  // ✅ YENİ: Kompresör seçimi
  const handleKompresorSec = useCallback((komp) => {
    setFormData({
      firma: komp.firma || '',
      model: komp.model || '',
      seriNumarasi: komp.seriNo || '',
      imalYili: komp.imalYili || '',
      detayliAciklama: ''
    });

    // Eğer daha önce part list varsa yükle
    if (komp.partList) {
      setBoxes(komp.partList.boxes || []);
      setLines(komp.partList.lines || []);
      toast.success(`✅ ${komp.firma} - ${komp.model} yüklendi (${(komp.partList.boxes || []).length} parça)`);
    } else {
      setBoxes([]);
      setLines([]);
      toast.success(`✅ ${komp.firma} - ${komp.model} seçildi`);
    }

    setShowKompresorSecim(false);
  }, []);

  // ✅ YENİ: Parça kütüphanesinden seç
  const handleParcaSec = useCallback((parca) => {
    const newBox = {
      id: Date.now(),
      x: 100,
      y: 100,
      width: 80,
      height: 40,
      partName: parca.ad,
      parcaDetaylari: {
        kod: parca.kod || '',
        kategori: parca.kategori || 'Diğer',
        boyut: parca.boyut || '',
        tedarikci: parca.tedarikci || '-',
        fiyat: parca.fiyat || 0,
        doviz: parca.doviz || '€',
        adet: 1,
        muadiller: parca.muadiller || [],
        pozisyon: '',
        durum: 'İyi',
        notlar: ''
      },
      details: { ...formData }
    };

    setBoxes(prev => [...prev, newBox]);
    setShowParcaKutuphanesi(false);
    toast.success(`✅ ${parca.ad} eklendi`);
  }, [formData]);

  // ✅ YENİ: Filtrelenmiş kompresörler
  const filtrelenmisKompresorler = useMemo(() => {
    if (!kompresorArama.trim()) return kompresorler;
    const searchTerm = kompresorArama.toLowerCase();
    return kompresorler.filter(k =>
      k.firma?.toLowerCase().includes(searchTerm) ||
      k.model?.toLowerCase().includes(searchTerm) ||
      k.seriNo?.toLowerCase().includes(searchTerm)
    );
  }, [kompresorler, kompresorArama]);

  // ✅ YENİ: Filtrelenmiş parçalar
  const filtrelenmisParcalar = useMemo(() => {
    let parcalar = [];
    Object.entries(parcaKutuphanesi).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        parcalar = [...parcalar, ...value];
      }
    });

    if (parcaKategori !== 'all') {
      parcalar = parcalar.filter(p => p.kategori === parcaKategori);
    }

    if (parcaArama.trim()) {
      const searchTerm = parcaArama.toLowerCase();
      parcalar = parcalar.filter(p =>
        p.ad?.toLowerCase().includes(searchTerm) ||
        p.kod?.toLowerCase().includes(searchTerm)
      );
    }

    return parcalar;
  }, [parcaKutuphanesi, parcaKategori, parcaArama]);

  // ✅ YENİ: Filtrelenmiş kutular (part list için)
  const filtrelenmisBoxes = useMemo(() => {
    return boxes.filter(box => {
      const matchSearch = !searchTerm ||
        box.partName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        box.parcaDetaylari?.kod?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchKategori = filterKategori === 'all' ||
        box.parcaDetaylari?.kategori === filterKategori;

      const matchDurum = filterDurum === 'all' ||
        box.parcaDetaylari?.durum === filterDurum;

      return matchSearch && matchKategori && matchDurum;
    });
  }, [boxes, searchTerm, filterKategori, filterDurum]);

  // ✅ YENİ: Toplam maliyet
  const toplamMaliyet = useMemo(() => {
    return boxes.reduce((toplam, box) => {
      const fiyat = box.parcaDetaylari?.fiyat || 0;
      const adet = box.parcaDetaylari?.adet || 1;
      return toplam + (fiyat * adet);
    }, 0);
  }, [boxes]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === 'firma') {
      setShowFirmaSuggestions(value.trim() !== '');
    }
  }, []);

  const handleFirmaSelect = useCallback((firmaAdi) => {
    setFormData(prev => ({ ...prev, firma: firmaAdi }));
    setShowFirmaSuggestions(false);
  }, []);

  const handleClick = useCallback((e) => {
    if (!imageContainerRef.current) return;

    const rect = imageContainerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    if (points.length === 0) {
      setPoints([x, y]);
    } else {
      const startX = points[0];
      const startY = points[1];
      const newLine = { start: [startX, startY], end: [x, y] };

      setLines(prevLines => [...prevLines, newLine]);

      const fixedWidth = 80;
      const fixedHeight = 40;
      const boxX = x - fixedWidth / 2;
      const boxY = y - fixedHeight / 2;

      const newBox = {
        id: Date.now(),
        x: boxX,
        y: boxY,
        width: fixedWidth,
        height: fixedHeight,
        partName: `Parça ${boxes.length + 1}`,
        parcaDetaylari: {
          kod: '',
          kategori: 'Diğer',
          boyut: '',
          tedarikci: '-',
          fiyat: 0,
          doviz: '€',
          adet: 1,
          muadiller: [],
          pozisyon: '',
          durum: 'İyi',
          notlar: ''
        },
        details: { ...formData }
      };

      setBoxes(prevBoxes => [...prevBoxes, newBox]);
      setPoints([]);
    }
  }, [points, boxes, formData]);

  const deleteBox = useCallback((index) => {
    setBoxes(prevBoxes => prevBoxes.filter((_, i) => i !== index));
    setLines(prevLines => prevLines.filter((_, i) => i !== index));
    toast.success('✅ Parça silindi');
  }, []);

  const updateSize = useCallback((index, w, h) => {
    setBoxes(prevBoxes => {
      const updatedBoxes = [...prevBoxes];
      updatedBoxes[index] = { ...updatedBoxes[index], width: w, height: h };
      return updatedBoxes;
    });
  }, []);

  const updatePosition = useCallback((index, x, y) => {
    setBoxes(prevBoxes => {
      const updatedBoxes = [...prevBoxes];
      updatedBoxes[index] = { ...updatedBoxes[index], x, y };
      return updatedBoxes;
    });

    setLines(prevLines => {
      const updatedLines = [...prevLines];
      if (updatedLines[index]) {
        updatedLines[index] = {
          ...updatedLines[index],
          end: [x + (boxes[index].width / 2), y + (boxes[index].height / 2)]
        };
      }
      return updatedLines;
    });
  }, [boxes]);

  const handlePartNameChange = useCallback((index, value) => {
    setBoxes(prevBoxes => {
      const updatedBoxes = [...prevBoxes];
      updatedBoxes[index] = { ...updatedBoxes[index], partName: value };
      return updatedBoxes;
    });
  }, []);

  // ✅ YENİ: Parça detaylarını güncelle
  const updateBoxParcaDetay = useCallback((index, field, value) => {
    setBoxes(prevBoxes => {
      const updatedBoxes = [...prevBoxes];
      updatedBoxes[index] = {
        ...updatedBoxes[index],
        parcaDetaylari: {
          ...updatedBoxes[index].parcaDetaylari,
          [field]: value
        }
      };
      return updatedBoxes;
    });
  }, []);

  // ✅ YENİ: Kutu düzenleme modal'ını aç
  const handleBoxClick = useCallback((index) => {
    setSelectedBoxIndex(index);
    setEditModalOpen(true);
  }, []);

  const saveCurrentState = useCallback(async () => {
    if (!formData.firma || !formData.model || !formData.seriNumarasi) {
      toast.error('⚠️ Lütfen Firma, Model ve Seri Numarası alanlarını doldurun.');
      return;
    }

    setIsLoading(true);
    setError(null);

    const newRecord = {
      id: Date.now(),
      name: `${formData.firma} - ${formData.model} (${formData.seriNumarasi})`,
      data: {
        boxes: JSON.parse(JSON.stringify(boxes)),
        lines: JSON.parse(JSON.stringify(lines)),
        formData: JSON.parse(JSON.stringify(formData))
      }
    };

    try {
      const res = await fetch('/api/records', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newRecord)
      });

      if (!res.ok) throw new Error(`API error: ${res.status}`);

      const saved = await res.json();
      setSavedRecords(prevRecords => [...prevRecords, saved]);

      if (!firmalar.includes(formData.firma)) {
        setFirmalar(prev => [...prev, formData.firma].sort());
      }

      toast.success('✅ Kayıt başarıyla oluşturuldu!');
    } catch (err) {
      setError('Kayıt yapılamadı. Lütfen daha sonra tekrar deneyin.');
      console.error('Kayıt hatası:', err);
      toast.error('❌ Kayıt yapılamadı');
    } finally {
      setIsLoading(false);
    }
  }, [boxes, lines, formData, firmalar]);

  // ✅ YENİ: Part list'i kompresöre kaydet
  const savePartListToKompresor = useCallback(async () => {
    if (!formData.seriNumarasi) {
      toast.error('⚠️ Lütfen önce bir kompresör seçin.');
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch(`/api/kayitlar/${formData.seriNumarasi}/partlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          boxes,
          lines,
          partListData: {
            toplamMaliyet,
            parcaSayisi: boxes.length,
            sonGuncelleme: new Date().toISOString()
          }
        })
      });

      if (!res.ok) throw new Error(`API error: ${res.status}`);

      toast.success('✅ Part list kompresöre kaydedildi!');
      fetchKompresorler(); // Listeyi yenile
    } catch (err) {
      console.error('Kaydetme hatası:', err);
      toast.error('❌ Kaydedilemedi');
    } finally {
      setIsLoading(false);
    }
  }, [formData.seriNumarasi, boxes, lines, toplamMaliyet, fetchKompresorler]);

  const loadRecord = useCallback((record) => {
    if (!record || !record.data) {
      toast.error('❌ Geçersiz kayıt verisi.');
      return;
    }

    setBoxes(record.data.boxes || []);
    setLines(record.data.lines || []);
    setFormData(record.data.formData || {});
    setShowRecordsPanel(false);
    toast.success('✅ Kayıt yüklendi');
  }, []);

  const deleteRecord = useCallback(async (id) => {
    if (!window.confirm('Bu kaydı silmek istediğinize emin misiniz?')) return;

    setIsLoading(true);
    setError(null);

    try {
      const res = await fetch(`/api/records/${id}`, {
        method: 'DELETE'
      });

      if (!res.ok) throw new Error(`API error: ${res.status}`);

      setSavedRecords(prevRecords => prevRecords.filter(r => r.id !== id));
      toast.success('✅ Kayıt silindi');
    } catch (err) {
      setError('Silme işlemi başarısız oldu.');
      console.error('Silme hatası:', err);
      toast.error('❌ Silinemedi');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ✅ YENİ: PDF Export
  const exportPDF = useCallback(() => {
    if (boxes.length === 0) {
      toast.error('⚠️ Henüz parça eklenmemiş.');
      return;
    }

    const doc = new jsPDF();

    // Başlık
    doc.setFontSize(20);
    doc.text('KOMPRESÖR REVİZYON PART LİSTESİ', 105, 20, { align: 'center' });

    // Firma bilgileri
    doc.setFontSize(12);
    doc.text(`Firma: ${formData.firma}`, 20, 40);
    doc.text(`Model: ${formData.model}`, 20, 50);
    doc.text(`Seri No: ${formData.seriNumarasi}`, 20, 60);
    doc.text(`İmal Yılı: ${formData.imalYili || '-'}`, 20, 70);
    doc.text(`Tarih: ${new Date().toLocaleDateString('tr-TR')}`, 150, 40);

    // Parça tablosu
    const tableData = boxes.map((box, i) => [
      i + 1,
      box.partName,
      box.parcaDetaylari?.kod || '-',
      box.parcaDetaylari?.kategori || '-',
      box.parcaDetaylari?.pozisyon || '-',
      box.parcaDetaylari?.adet || 1,
      `${box.parcaDetaylari?.fiyat || 0} €`,
      `${(box.parcaDetaylari?.fiyat || 0) * (box.parcaDetaylari?.adet || 1)} €`,
      box.parcaDetaylari?.tedarikci || '-',
      box.parcaDetaylari?.durum || '-'
    ]);

    autoTable(doc, {
      startY: 90,
      head: [['Poz', 'Parça', 'Kod', 'Kategori', 'Pozisyon', 'Adet', 'Birim', 'Toplam', 'Tedarikçi', 'Durum']],
      body: tableData,
      theme: 'grid',
      headStyles: { fillColor: [30, 58, 138] },
      styles: { fontSize: 8 }
    });

    // Toplam
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(12);
    doc.text(`TOPLAM MALİYET: ${toplamMaliyet.toFixed(2)} €`, 150, finalY, { align: 'right' });
    doc.text(`TOPLAM PARÇA: ${boxes.length}`, 150, finalY + 10, { align: 'right' });

    // Notlar
    if (formData.detayliAciklama) {
      doc.text('NOTLAR:', 20, finalY + 30);
      doc.setFontSize(10);
      const splitText = doc.splitTextToSize(formData.detayliAciklama, 170);
      doc.text(splitText, 20, finalY + 40);
    }

    // Footer
    doc.setFontSize(8);
    doc.text('MOON X24 AIR MAKİNA - www.airmakina.com', 105, 280, { align: 'center' });

    doc.save(`${formData.firma}_${formData.model}_PartList.pdf`);
    toast.success('✅ PDF oluşturuldu');
  }, [boxes, formData, toplamMaliyet]);

  const filteredFirmalar = useMemo(() => {
    if (!formData.firma.trim()) return firmalar;
    return firmalar.filter(f =>
      f.toLowerCase().includes(formData.firma.toLowerCase())
    );
  }, [formData.firma, firmalar]);

  if (isLoading && boxes.length === 0) {
    return (
      <div className="compressor-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Veriler yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="compressor-container">
      {error && (
        <div className="error-message">
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* ✅ YENİ: Üst Araç Çubuğu */}
      <div className="toolbar">
        <button
          className="toolbar-btn"
          onClick={() => setShowKompresorSecim(true)}
          title="Kompresör Seç"
        >
          📋 Kompresör Seç
        </button>
        <button
          className="toolbar-btn"
          onClick={() => setShowParcaKutuphanesi(true)}
          title="Parça Kütüphanesi"
        >
          🔧 Parça Kütüphanesi
        </button>
        <button
          className="toolbar-btn"
          onClick={() => setShowPartList(!showPartList)}
          title="Part List"
        >
          📊 Part List
        </button>
        <button
          className="toolbar-btn"
          onClick={exportPDF}
          title="PDF Export"
        >
          <FileText className="w-4 h-4" /> PDF
        </button>
        <button
          className="toolbar-btn"
          onClick={savePartListToKompresor}
          disabled={isLoading}
          title="Kompresöre Kaydet"
        >
          <Save className="w-4 h-4" /> Kaydet
        </button>
        <button
          className="toolbar-btn"
          onClick={() => setShowRecordsPanel(true)}
          title="Tüm Kayıtlar"
        >
          💾 Kayıtlar
        </button>
      </div>

      <div className="main-content">
        <div className="image-container" ref={imageContainerRef}>
          <img
            src="/images/compressor.png"
            alt="Kompresör"
            onClick={handleClick}
          />

          <svg className="lines-layer">
            {lines.map((line, index) => (
              <line
                key={`line-${index}`}
                x1={line.start[0]}
                y1={line.start[1]}
                x2={line.end[0]}
                y2={line.end[1]}
                stroke="#ff4136"
                strokeWidth="2"
                strokeDasharray="3,3"
              />
            ))}
          </svg>

          {boxes.map((box, index) => (
            <ResizableBox
              key={`box-${box.id}`}
              initialX={box.x}
              initialY={box.y}
              initialWidth={box.width}
              initialHeight={box.height}
              onResize={(w, h) => updateSize(index, w, h)}
              onDrag={(x, y) => updatePosition(index, x, y)}
            >
              <div
                className="box-content"
                onClick={(e) => {
                  e.stopPropagation();
                  handleBoxClick(index);
                }}
              >
                <div className="flex justify-between items-center mb-2">
                  <h4 className="text-sm font-semibold text-gray-700 truncate">
                    {box.partName}
                  </h4>
                </div>

                <div className="mb-2">
                  <label className="text-xs font-semibold block text-gray-600 mb-1">Parça Adı:</label>
                  <input
                    type="text"
                    value={box.partName}
                    onChange={(e) => handlePartNameChange(index, e.target.value)}
                    className="w-full p-1.5 text-sm border border-gray-300 rounded"
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                  />
                </div>

                {box.parcaDetaylari?.kod && (
                  <div className="parca-kod-badge">
                    {box.parcaDetaylari.kod}
                  </div>
                )}

                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteBox(index);
                  }}
                  className="w-full bg-red-500 hover:bg-red-600 text-white text-xs py-1.5 px-2 rounded transition-colors"
                >
                  ❌ Sil
                </button>
              </div>
            </ResizableBox>
          ))}
        </div>

        <div className="detail-container">
          <div className="detail-panel">
            <div
              className="detail-header"
              onClick={() => setShowDetails(prev => !prev)}
            >
              <span>Detay Kutusu</span>
              <span>{showDetails ? '🔼' : '🔽'}</span>
            </div>

            {showDetails && (
              <form onSubmit={(e) => e.preventDefault()} className="relative">
                <div className="form-group">
                  <label>FİRMA:</label>
                  <input
                    name="firma"
                    value={formData.firma}
                    onChange={handleInputChange}
                    onFocus={() => setShowFirmaSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowFirmaSuggestions(false), 200)}
                    placeholder="Firma adı"
                    autoComplete="off"
                  />
                  {showFirmaSuggestions && filteredFirmalar.length > 0 && (
                    <ul className="firma-suggestions">
                      {filteredFirmalar.map((firma, idx) => (
                        <li
                          key={idx}
                          onClick={() => handleFirmaSelect(firma)}
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          {firma}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="form-group">
                  <label>MODEL:</label>
                  <input
                    name="model"
                    value={formData.model}
                    onChange={handleInputChange}
                    placeholder="Kompresör modeli"
                  />
                </div>

                <div className="form-group">
                  <label>K. SERİ NUMARASI:</label>
                  <input
                    name="seriNumarasi"
                    value={formData.seriNumarasi}
                    onChange={handleInputChange}
                    placeholder="Seri numarası"
                  />
                </div>

                <div className="form-group">
                  <label>İMAL YILI:</label>
                  <input
                    name="imalYili"
                    value={formData.imalYili}
                    onChange={handleInputChange}
                    placeholder="İmal yılı"
                    type="number"
                  />
                </div>

                <div className="form-group">
                  <label>DETAYLI AÇIKLAMA:</label>
                  <textarea
                    name="detayliAciklama"
                    value={formData.detayliAciklama}
                    onChange={handleInputChange}
                    rows={4}
                    placeholder="Detaylı açıklama"
                  />
                </div>

                {/* ✅ YENİ: Maliyet Özeti */}
                <div className="cost-summary">
                  <h4>💰 Maliyet Özeti</h4>
                  <p>Toplam Parça: <strong>{boxes.length}</strong></p>
                  <p>Toplam Maliyet: <strong>{toplamMaliyet.toFixed(2)} €</strong></p>
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    onClick={saveCurrentState}
                    disabled={isLoading}
                  >
                    {isLoading ? 'İşleniyor...' : '➕ Yeni Kayıt Ekle'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* ✅ YENİ: Kompresör Seçim Modal */}
      {showKompresorSecim && (
        <div className="modal-overlay" onClick={() => setShowKompresorSecim(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>📋 Kompresör Seç</h3>
            <input
              type="text"
              placeholder="Firma, model veya seri no ara..."
              value={kompresorArama}
              onChange={(e) => setKompresorArama(e.target.value)}
              className="modal-search"
            />
            <div className="kompresor-listesi">
              {filtrelenmisKompresorler.length === 0 ? (
                <p className="empty-message">Kompresör bulunamadı.</p>
              ) : (
                filtrelenmisKompresorler.map((k) => (
                  <div
                    key={k.seriNo}
                    className="kompresor-item"
                    onClick={() => handleKompresorSec(k)}
                  >
                    <strong>{k.firma}</strong> - {k.model} ({k.seriNo})
                    {k.partList && (
                      <span className="partlist-badge">
                        📊 {k.partList.parcaSayisi || 0} parça
                      </span>
                    )}
                  </div>
                ))
              )}
            </div>
            <button className="modal-close-btn" onClick={() => setShowKompresorSecim(false)}>
              Kapat
            </button>
          </div>
        </div>
      )}

      {/* ✅ YENİ: Parça Kütüphanesi Modal */}
      {showParcaKutuphanesi && (
        <div className="modal-overlay" onClick={() => setShowParcaKutuphanesi(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <h3>🔧 Parça Kütüphanesi</h3>
            <div className="parca-filtreler">
              <input
                type="text"
                placeholder="Parça ara..."
                value={parcaArama}
                onChange={(e) => setParcaArama(e.target.value)}
                className="modal-search"
              />
              <select
                value={parcaKategori}
                onChange={(e) => setParcaKategori(e.target.value)}
                className="kategori-select"
              >
                {PARCA_KATEGORILERI.map(k => (
                  <option key={k.value} value={k.value}>{k.label}</option>
                ))}
              </select>
            </div>
            <div className="parca-listesi">
              {filtrelenmisParcalar.length === 0 ? (
                <p className="empty-message">Parça bulunamadı.</p>
              ) : (
                filtrelenmisParcalar.map((parca, i) => (
                  <div
                    key={i}
                    className="parca-item"
                    onClick={() => handleParcaSec(parca)}
                  >
                    <div className="parca-info">
                      <strong>{parca.ad}</strong>
                      <span className="parca-kod">{parca.kod}</span>
                      <span className="parca-kategori">{parca.kategori}</span>
                    </div>
                    <div className="parca-details">
                      <span className="parca-fiyat">{parca.fiyat} {parca.doviz}</span>
                      <span className="parca-tedarikci">{parca.tedarikci}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
            <button className="modal-close-btn" onClick={() => setShowParcaKutuphanesi(false)}>
              Kapat
            </button>
          </div>
        </div>
      )}

      {/* ✅ YENİ: Parça Detay Düzenleme Modal */}
      {editModalOpen && selectedBoxIndex !== null && boxes[selectedBoxIndex] && (
        <div className="modal-overlay" onClick={() => setEditModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>🔧 Parça Detayları</h3>
            <div className="edit-form">
              <div className="form-group">
                <label>Parça Adı</label>
                <input
                  type="text"
                  value={boxes[selectedBoxIndex].partName}
                  onChange={(e) => {
                    setBoxes(prev => {
                      const updated = [...prev];
                      updated[selectedBoxIndex] = { ...updated[selectedBoxIndex], partName: e.target.value };
                      return updated;
                    });
                  }}
                />
              </div>

              <div className="form-group">
                <label>Pozisyon (Nerede?)</label>
                <select
                  value={boxes[selectedBoxIndex].parcaDetaylari?.pozisyon || ''}
                  onChange={(e) => updateBoxParcaDetay(selectedBoxIndex, 'pozisyon', e.target.value)}
                >
                  <option value="">Seçiniz...</option>
                  {POZISYONLAR.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Adet</label>
                <input
                  type="number"
                  min="1"
                  value={boxes[selectedBoxIndex].parcaDetaylari?.adet || 1}
                  onChange={(e) => updateBoxParcaDetay(selectedBoxIndex, 'adet', parseInt(e.target.value))}
                />
              </div>

              <div className="form-group">
                <label>Durum</label>
                <select
                  value={boxes[selectedBoxIndex].parcaDetaylari?.durum || 'İyi'}
                  onChange={(e) => updateBoxParcaDetay(selectedBoxIndex, 'durum', e.target.value)}
                >
                  {DURUMLAR.map(d => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Notlar</label>
                <textarea
                  rows={3}
                  value={boxes[selectedBoxIndex].parcaDetaylari?.notlar || ''}
                  onChange={(e) => updateBoxParcaDetay(selectedBoxIndex, 'notlar', e.target.value)}
                  placeholder="Parça hakkında notlar..."
                />
              </div>

              {boxes[selectedBoxIndex].parcaDetaylari?.muadiller?.length > 0 && (
                <div className="muadiller-section">
                  <label>Muadiller:</label>
                  <div className="muadil-chips">
                    {boxes[selectedBoxIndex].parcaDetaylari.muadiller.map((m, i) => (
                      <span key={i} className="muadil-chip">{m}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <button className="modal-close-btn" onClick={() => setEditModalOpen(false)}>
              Kapat
            </button>
          </div>
        </div>
      )}

      {/* ✅ YENİ: Part List Tablosu */}
      {showPartList && (
        <div className="part-list-panel">
          <div className="part-list-header">
            <h3>📋 Part List - {formData.firma} - {formData.model}</h3>
            <div className="part-list-filters">
              <input
                type="text"
                placeholder="Parça ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              <select
                value={filterKategori}
                onChange={(e) => setFilterKategori(e.target.value)}
                className="filter-select"
              >
                {PARCA_KATEGORILERI.map(k => (
                  <option key={k.value} value={k.value}>{k.label}</option>
                ))}
              </select>
              <select
                value={filterDurum}
                onChange={(e) => setFilterDurum(e.target.value)}
                className="filter-select"
              >
                <option value="all">Tüm Durumlar</option>
                {DURUMLAR.map(d => (
                  <option key={d.value} value={d.value}>{d.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="part-list-table-container">
            <table className="part-list-table">
                            <thead>
                <tr>
                  <th>Poz</th>
                  <th>Parça Adı</th>
                  <th>Kod</th>
                  <th>Kategori</th>
                  <th>Pozisyon</th>
                  <th>Adet</th>
                  <th>Birim Fiyat</th>
                  <th>Toplam</th>
                  <th>Tedarikçi</th>
                  <th>Durum</th>
                  <th>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {filtrelenmisBoxes.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="empty-row">Parça bulunamadı.</td>
                  </tr>
                ) : (
                  filtrelenmisBoxes.map((box, i) => {
                    const originalIndex = boxes.indexOf(box);
                    const birimFiyat = box.parcaDetaylari?.fiyat || 0;
                    const adet = box.parcaDetaylari?.adet || 1;
                    const toplam = birimFiyat * adet;
                    const durumBilgisi = DURUMLAR.find(d => d.value === box.parcaDetaylari?.durum);

                    return (
                      <tr key={box.id}>
                        <td>{i + 1}</td>
                        <td className="parca-adi-cell">{box.partName}</td>
                        <td className="kod-cell">{box.parcaDetaylari?.kod || '-'}</td>
                        <td>{box.parcaDetaylari?.kategori || '-'}</td>
                        <td>{box.parcaDetaylari?.pozisyon || '-'}</td>
                        <td className="adet-cell">{adet}</td>
                        <td className="fiyat-cell">{birimFiyat.toFixed(2)} €</td>
                        <td className="fiyat-cell">{toplam.toFixed(2)} €</td>
                        <td>{box.parcaDetaylari?.tedarikci || '-'}</td>
                        <td>
                          <span 
                            className="durum-badge" 
                            style={{ backgroundColor: durumBilgisi?.color || '#94a3b8' }}
                          >
                            {box.parcaDetaylari?.durum || '-'}
                          </span>
                        </td>
                        <td>
                          <button 
                            className="btn-edit-small"
                            onClick={() => handleBoxClick(originalIndex)}
                            title="Düzenle"
                          >
                            ✏️
                          </button>
                          <button 
                            className="btn-delete-small"
                            onClick={() => deleteBox(originalIndex)}
                            title="Sil"
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
              <tfoot>
                <tr className="total-row">
                  <td colSpan="5"><strong>TOPLAM</strong></td>
                  <td className="adet-cell">
                    <strong>{filtrelenmisBoxes.reduce((t, b) => t + (b.parcaDetaylari?.adet || 1), 0)}</strong>
                  </td>
                  <td>-</td>
                  <td className="fiyat-cell">
                    <strong>{toplamMaliyet.toFixed(2)} €</strong>
                  </td>
                  <td colSpan="3"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Kayıtlar Paneli */}
      {showRecordsPanel && (
        <div className="records-panel records-panel-open">
          <div className="panel-content">
            <div className="panel-header">
              <h3>💾 Kayıtlar</h3>
              <button onClick={() => setShowRecordsPanel(false)} aria-label="Close panel">
                &times;
              </button>
            </div>

            {isLoading ? (
              <div className="loading-spinner">
                <div className="spinner"></div>
              </div>
            ) : savedRecords.length === 0 ? (
              <div className="empty-records">Henüz kayıt bulunmuyor.</div>
            ) : (
              <ul className="records-list">
                {savedRecords.map((record) => (
                  <li key={record.id}>
                    <div className="record-name" onClick={() => loadRecord(record)}>
                      {record.name}
                    </div>
                    <div className="record-actions">
                      <button onClick={() => loadRecord(record)}>Yükle</button>
                      <button onClick={() => deleteRecord(record.id)}>Sil</button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {showRecordsPanel && (
        <div onClick={() => setShowRecordsPanel(false)} className="panel-overlay" />
      )}
    </div>
  );
};

export default CompressorPartTracker;